import type { FC } from "react";
import PropTypes from "prop-types";
import ChevronDownIcon from "@untitled-ui/icons-react/build/esm/ChevronDown";
import type { SxProps } from "@mui/system/styleFunctionSx";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";

import useUserDataProvider from "src/contexts/snug/userData-context";
import { useNetworkAccounts } from "src/hooks/use-network-accounts";
import { usePopover } from "src/hooks/use-popover";
import { groupAccountsByNetwork } from "src/utils/snug/network-session";

interface NetworkSwitchProps {
  sx?: SxProps;
}

// Lets a network admin hop between the pro groups in their network. Renders nothing for
// everyone else (no network accounts).
export const NetworkSwitch: FC<NetworkSwitchProps> = (props) => {
  const { sx } = props;
  const popover = usePopover<HTMLButtonElement>();
  const { professionalGroup } = useUserDataProvider();
  const { network, switchingRoleId, switchTo, backToNetwork } = useNetworkAccounts();

  if (!network || network.accounts.length === 0) {
    return null;
  }

  const isCurrent = (pro_group_id: string, owner_ud_id: string) =>
    pro_group_id === professionalGroup?.id ||
    (!!network.current_ud_id && owner_ud_id === network.current_ud_id);

  const current = network.accounts.find((a) => isCurrent(a.pro_group_id, a.owner_ud_id));
  const others = network.accounts.filter((a) => !isCurrent(a.pro_group_id, a.owner_ud_id));
  const groups = groupAccountsByNetwork(others);
  const showNetworkNames = groupAccountsByNetwork(network.accounts).length > 1;

  return (
    <>
      <ButtonBase
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        sx={{
          alignItems: "center",
          border: "1px solid #e7e7e7",
          borderRadius: 1,
          display: "flex",
          gap: 1,
          px: 1.5,
          py: 1,
          textAlign: "left",
          width: "100%",
          ...sx,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            color="text.secondary"
            noWrap
            variant="caption"
            component="div"
          >
            {current?.network_name || "Network"}
          </Typography>
          <Typography
            color="text.primary"
            noWrap
            variant="subtitle2"
          >
            {professionalGroup?.name || current?.pro_group_name || "Choose a pro group"}
          </Typography>
        </Box>
        <SvgIcon
          sx={{ fontSize: 16, color: "text.secondary" }}
        >
          <ChevronDownIcon />
        </SvgIcon>
      </ButtonBase>
      <Menu
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        onClose={popover.handleClose}
        open={popover.open}
        PaperProps={{ sx: { width: 260, maxHeight: 420 } }}
      >
        {/* Menu doesn't accept Fragments, so flatten the network groups */}
        {groups.flatMap((group) => [
          ...(showNetworkNames
            ? [
                <ListSubheader
                  key={`network-${group.network_id}`}
                  sx={{ lineHeight: "32px" }}
                >
                  {group.network_name}
                </ListSubheader>,
              ]
            : []),
          ...group.accounts.map((account) => (
            <MenuItem
              key={account.role_id}
              disabled={!!switchingRoleId}
              onClick={() => switchTo(account.role_id)}
              sx={{ gap: 1.5 }}
            >
              <Avatar
                src={account.logo || undefined}
                variant="rounded"
                sx={{ bgcolor: "neutral.100", color: "text.secondary", width: 28, height: 28 }}
                imgProps={{ style: { objectFit: "contain" } }}
              >
                <BusinessOutlinedIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography
                noWrap
                variant="body2"
              >
                {account.pro_group_name}
              </Typography>
            </MenuItem>
          )),
        ])}
        {groups.length > 0 && <Divider />}
        <MenuItem
          onClick={() => {
            popover.handleClose();
            backToNetwork();
          }}
          sx={{ gap: 1.5 }}
        >
          <ArrowBackRoundedIcon fontSize="small" />
          <Typography variant="body2">Back to network</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

NetworkSwitch.propTypes = {
  // @ts-ignore
  sx: PropTypes.object,
};
